import GuideLayout from '../../layouts/GuideLayout'
import type { TocItem } from '../../layouts/GuideLayout'
import { AffiliateCTA } from '../../components/content/AffiliateCTA'
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed'

const tocItems: TocItem[] = [
  { id: 'token-pricing',       label: 'Token-based pricing: what a token is',      level: 2 },
  { id: 'model-selection',     label: 'Model selection: GPT-4o vs mini vs o3',     level: 2 },
  { id: 'standard-payg',       label: 'Standard PAYG: when it is the right choice', level: 2 },
  { id: 'ptu-reservations',    label: 'PTU reservations: the 7-day break-even rule', level: 2 },
  { id: 'image-vision-tokens', label: 'Image and vision tokens',                   level: 2 },
  { id: 'estimating-usage',    label: 'Practical estimation before deploying',     level: 2 },
  { id: 'calculator',          label: 'Azure OpenAI Calculator',                   level: 2 },
]

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-900 Microsoft Azure Fundamentals"
    url="https://www.pluralsight.com/paths/az-900-microsoft-azure-fundamentals"
    price="From £29/month"
    badgeText="Azure AI · OpenAI · GPT-4o"
  />
)

export default function AzureOpenAIPricingGuide() {
  return (
    <GuideLayout
      title="Azure OpenAI Pricing Guide UK 2026"
      description="Azure OpenAI has two pricing models that work completely differently from each other and from every other Azure service. Standard (PAYG) charges per token — you pay only for what you use, it scales to zero, and the cost is entirely predictable per request. Provisioned Throughput Units (PTUs) charge per hour regardless of actual usage, like reserving a slot at a restaurant whether or not you show up. PTUs save money only if your application sustains load for roughly 7 or more days per month. Below that threshold, PAYG is cheaper. Microsoft published a dedicated blog post titled &ldquo;Do PTUs Save You Money?&rdquo; specifically because this calculation confuses engineers — that is a signal worth paying attention to."
      lastUpdated="April 2026"
      canonical="https://www.azure-calc.co.uk/guides/azure-openai-pricing-uk"
      metaTitle="Azure OpenAI Pricing Guide UK 2026 | GPT-4o · PTU vs PAYG | AzureCalc.uk"
      metaDescription="Understand Azure OpenAI pricing in GBP for UK developers. Covers Standard PAYG token billing, PTU break-even analysis, GPT-4o vs GPT-4o mini cost comparison, and how to estimate your usage."
      tocItems={tocItems}
      relatedCalculators={[
        { service: 'azure-openai', path: '/azure-openai', label: 'Azure OpenAI Calculator' },
      ]}
      bottomCta={bottomCta}
    >
      <h2 id="token-pricing">Token-based pricing: what a token is</h2>
      <p>
        Everything you send to and receive from Azure OpenAI is billed in tokens. A token is roughly four characters of
        English text, or about three-quarters of a word. The phrase &ldquo;Azure OpenAI is expensive&rdquo; contains seven
        words and approximately nine tokens. Longer contexts — sending previous conversation history as part of a prompt —
        rapidly increase your input token count and therefore your costs.
      </p>
      <p>
        Input tokens and output tokens are priced separately, and output tokens consistently cost more — typically 3–4× the
        input rate. This reflects the computational cost of generation versus reading. For GPT-4o, input costs £0.0019 per
        1,000 tokens and output costs £0.0075 per 1,000 tokens (April 2026). A chat application that sends 1 million input
        tokens and generates 200,000 output tokens per day costs approximately £80/month.
      </p>
      <p>
        Unlike compute-based services where you pay for time, token billing is purely consumption-based. An idle deployment
        costs nothing. A spike in usage costs exactly proportionally more. This is the primary appeal of Standard PAYG for
        most production workloads.
      </p>

      <h2 id="model-selection">Model selection: GPT-4o vs mini vs o3</h2>
      <p>
        The choice of model is the single biggest cost lever you have. GPT-4o mini costs approximately 5% of GPT-4o&apos;s
        input rate (£0.0001 vs £0.0019 per 1K tokens). For high-volume classification, summarisation, or routing tasks
        that do not require frontier capability, GPT-4o mini is almost always the right choice.
      </p>
      <p>
        GPT-4o is appropriate when you need the full capability — complex reasoning, nuanced language, or tasks where
        accuracy directly affects business outcomes. The quality gap between GPT-4o and GPT-4o mini is significant for
        complex tasks but negligible for straightforward extraction or classification.
      </p>
      <p>
        o3-mini is a reasoning model in the o-series family. It thinks before responding, which improves accuracy on
        mathematical, scientific, and multi-step logical tasks. The trade-off is higher latency and higher cost than
        GPT-4o-mini, though still below GPT-4o at £0.0008 input / £0.0033 output per 1K tokens. Use it for tasks
        that specifically benefit from explicit chain-of-thought reasoning.
      </p>
      <p>
        GPT-4 (8K context, legacy) at £0.0226/£0.0451 per 1K tokens is significantly more expensive than GPT-4o for
        equivalent quality. New deployments should use GPT-4o. GPT-4 legacy pricing reflects older infrastructure costs
        and will eventually be retired.
      </p>

      <h2 id="standard-payg">Standard PAYG: when it is the right choice</h2>
      <p>
        Standard PAYG is the correct choice for the majority of Azure OpenAI workloads. It has no minimum commitment,
        scales to zero during idle periods, and is completely predictable — every request costs a known amount based on
        its token count. For a new deployment, PAYG eliminates the risk of committing to capacity before you understand
        your actual usage patterns.
      </p>
      <p>
        PAYG becomes less attractive when your workload is predictable and continuous. If your application handles
        roughly constant load throughout the working day and you are approaching the break-even point for PTU
        reservations, the capacity guarantees of PTU start to matter alongside the cost argument.
      </p>
      <p>
        The practical threshold: if your deployment is likely to be idle for more than half the days in a month
        (for example, a batch processing job that runs every other week), Standard PAYG is almost certainly cheaper.
        Use the <a href="/azure-openai">calculator</a> to model your specific token volumes against PTU rates.
      </p>

      <h2 id="ptu-reservations">PTU reservations: the 7-day break-even rule</h2>
      <p>
        A Provisioned Throughput Unit (PTU) reserves dedicated inference capacity for your deployment. You are charged
        per PTU per hour regardless of whether you use that capacity. For GPT-4o, 1 PTU provides approximately
        6 tokens per second of sustained throughput.
      </p>
      <p>
        The break-even analysis is straightforward. PTU monthly cost is fixed: 100 PTUs at the global rate of
        £0.7524/hr × 730 hours = £549.25/month. Standard PAYG monthly cost varies with usage. At 1 million input
        tokens and 200,000 output tokens per day, Standard costs approximately £80/month — so PTU would cost 7×
        more. At 10 million input tokens and 2 million output tokens per day, Standard costs approximately £800/month,
        at which point 100 PTUs become the cheaper option.
      </p>
      <p>
        The &ldquo;7 days&rdquo; rule of thumb reflects the observation that most workloads do not sustain
        uniform load across all 30 days. Weekends, holidays, maintenance windows, and traffic variability mean
        that a deployment running at full capacity for 22 working days still has 8 idle days where PTU capacity
        is wasted. The break-even point — where PTU monthly cost equals the Standard PAYG cost of your actual
        usage — often falls around 7–10 active days for typical production workloads.
      </p>
      <p>
        PTU commitment terms: hourly (no discount), 1-month (~5% saving), 1-year (~33% saving). Annual PTU
        commitments make most sense when you have confirmed that PTU is already cost-effective on an hourly basis.
      </p>

      <h2 id="image-vision-tokens">Image and vision tokens</h2>
      <p>
        Vision-capable models (GPT-4o, GPT-4o mini) can process images as inputs. Images are not billed in text
        tokens — they are priced per image at approximately £0.0577 per 1,080-pixel image (April 2026). Higher
        resolution images cost more. Images are split into 512×512 pixel tiles and each tile costs one tile fee.
      </p>
      <p>
        This often surprises engineers who assume image processing is an extension of text token billing. A
        pipeline that processes 100 product images per day would cost approximately £1.73/day for image inputs
        alone, regardless of the text tokens in the accompanying prompt. Factor this separately when estimating
        costs for document processing or visual search applications.
      </p>

      <h2 id="estimating-usage">Practical estimation before deploying</h2>
      <p>
        The hardest part of estimating Azure OpenAI costs is predicting token volumes before you have production
        data. A practical approach:
      </p>
      <ul>
        <li>
          <strong>Benchmark a representative sample:</strong> Take 50–100 representative requests and measure
          actual token counts using the model tokeniser. Multiply by your expected request volume.
        </li>
        <li>
          <strong>Account for context window growth:</strong> Conversational applications accumulate history in
          each request. A 10-turn conversation includes all previous messages — the 10th message might have
          10× the token count of the first. This compounds rapidly.
        </li>
        <li>
          <strong>Separate input from output:</strong> Output volume is often unpredictable. If you allow open-ended
          generation (no max_tokens limit), actual output can vary by 5–10× across requests. Set a max_tokens
          ceiling for cost predictability.
        </li>
        <li>
          <strong>Use Standard PAYG for the first month:</strong> Do not commit to PTU until you have at least
          30 days of production data. The insights from actual usage almost always change your estimate.
        </li>
      </ul>

      <h2 id="calculator">Azure OpenAI Calculator</h2>
      <p>
        Use the <a href="/azure-openai">Azure OpenAI Calculator →</a> to estimate your costs. The
        break-even analysis shows the number of active days per month at which PTU becomes cheaper than
        PAYG at your usage level.
      </p>

      <p className="text-[12px] italic" style={{ color: 'var(--text-tertiary)' }}>
        Built and verified by an independent Azure engineer.
      </p>

      <CalculatorEmbed service="azure-openai" />
    </GuideLayout>
  )
}
